import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, ShieldCheck, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PlatformLogo } from './PlatformLogo';

const GoogleReviewsSection = () => {
  const sampleReviews = [
    { name: 'Julien D.', rating: 5, text: 'Excellent restaurant, service impeccable. Je recommande vivement !', date: 'Il y a 2 jours', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face' },
    { name: 'Amélie R.', rating: 5, text: 'Une équipe très professionnelle. Rapport qualité prix imbattable.', date: 'Il y a 5 jours', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face' },
    { name: 'Karim B.', rating: 5, text: 'Résultats au top, service client réactif. Merci !', date: 'La semaine dernière', img: 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6?w=100&h=100&fit=crop&crop=face' }
  ];

  return (
    <section className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] bg-[#4285F4] rounded-full blur-[220px] opacity-10" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5">
              <PlatformLogo platform="google" className="w-4 h-4" />
              <span className="text-xs font-semibold text-white/80">Nouveau — Avis Google Business</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-5">
              Boostez la note de votre <span className="gradient-text">entreprise locale</span>.
            </h2>
            <p className="text-white/60 leading-relaxed mb-8 max-w-lg">
              Restaurants, artisans, e-commerçants : obtenez de vrais avis Google 5 étoiles, rédigés par des profils vérifiés, livrés progressivement sur votre fiche Google Business.
            </p>

            <div className="space-y-3 mb-8">
              {[
                'Avis rédigés par de vrais profils vérifiés dans le monde entier',
                'Livraison progressive et naturelle (5 à 30 jours)',
                'Compatible Google Business & Google Maps',
                'Garantie satisfait ou remplacement offert'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#4285F4]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4 text-[#4285F4]" />
                  </div>
                  <span className="text-white/80 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/collections/google" className="btn-primary inline-flex items-center gap-2 text-sm">
                Booster ma fiche Google <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#faq" className="btn-ghost text-sm">Comment ça marche ?</a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="glass-card rounded-3xl p-6 lg:p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#4285F4] rounded-full blur-3xl opacity-15" />

              <div className="relative flex items-center justify-between mb-6 pb-6 border-b border-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <PlatformLogo platform="google" className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">Chez Marco — Pizzeria</div>
                    <div className="flex items-center gap-2 text-white/50 text-xs">
                      <MapPin className="w-3 h-3" /> Worldwide · Google Business
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#4285F4]/15 border border-[#4285F4]/30">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#4285F4]" />
                  <span className="text-[#4285F4] text-xs font-semibold">Vérifié</span>
                </div>
              </div>

              <div className="relative grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-white/50 text-xs mb-1">Note globale</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>4.9</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-[#FBBC05]" fill="#FBBC05" />)}
                    </div>
                  </div>
                  <div className="text-white/50 text-xs mt-1">Sur 247 avis Google</div>
                </div>
                <div>
                  <div className="text-white/50 text-xs mb-1">Évolution 30 jours</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-[#00b67a]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>+87</span>
                    <TrendingUp className="w-5 h-5 text-[#00b67a]" />
                  </div>
                  <div className="text-white/50 text-xs mt-1">Nouveaux avis 5 étoiles</div>
                </div>
              </div>

              <div className="relative space-y-3">
                {sampleReviews.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <img src={r.img} alt={r.name} className="w-9 h-9 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold text-sm">{r.name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => <Star key={s} className="w-2.5 h-2.5 text-[#FBBC05]" fill="#FBBC05" />)}
                        </div>
                        <span className="text-white/40 text-[10px] ml-auto">{r.date}</span>
                      </div>
                      <p className="text-white/60 text-xs leading-relaxed line-clamp-2">{r.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
